import { useMutation } from '@tanstack/react-query';
import { applicationFormApi } from 'api/applicationForm';
import { Button, FormSelect, FormTextField } from 'components/common';
import { ISelectOption } from 'components/common/Select/types';
import { useNavigateBack } from 'hooks';
import { useApplicationInProgressById } from 'queries/applications/useApplicationInProgressById';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageURL } from 'router/pageURL';
import { getCurrentRoute } from 'utils/location';
import { parseApplicationInProgress } from '../helpers';
import { ApplicationFormPageEnum } from '../types';
import { industryAndRoleQuestions } from './questions';
import { IIndustryAndRoleForm } from './types';

export const IndustryAndRole = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const employment = searchParams.get('employment');
  const navigateBack = useNavigateBack();
  const { id: applicationId = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(applicationId);
  const formattedResponse =
    applicationInProgress?.[currentRoute] && parseApplicationInProgress(applicationInProgress[currentRoute]);

  const form = useForm<IIndustryAndRoleForm>({
    defaultValues: {
      industry: null,
      role: '',
    },
  });

  const formState = form.watch();

  useEffect(() => {
    if (!employment) navigate(PageURL.APPLICATIONS);
  }, [navigate, employment]);

  useEffect(() => {
    if (formattedResponse) form.reset(formattedResponse);
  }, [applicationInProgress]);

  const submitFormHandler = async (formData: IIndustryAndRoleForm) => {
    const questions = Object.entries(formData).map(([key, answer]) => ({
      question: intl.formatMessage({ id: industryAndRoleQuestions[key].labelKey }),
      key,
      answer,
    }));

    await patchApplicationInProgress({ questions, currentRoute, applicationId });
    searchParams.set('section', ApplicationFormPageEnum.EMPLOYMENT_TENURE);
    setSearchParams(searchParams);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        <div>
          <FormattedMessage id={industryAndRoleQuestions.industry.labelKey} tagName={'h4'} />
          <FormattedMessage id={industryAndRoleQuestions.industry.explanationKey} tagName={'p'} />
          <FormSelect
            name={'industry'}
            options={industryAndRoleQuestions.industry.options}
            className={'w-full'}
            selectProps={{
              isOptionEqualToValue: (option: ISelectOption, selectedOptionValue: string) =>
                option.value === selectedOptionValue,
              value: formState.industry,
            }}
            selectInputProps={{
              placeholder: intl.formatMessage({ id: 'placeholders.select' }),
            }}
            rules={{ required: intl.formatMessage({ id: 'common.error.requiredField' }) }}
          />
        </div>
        <div>
          <FormattedMessage id={industryAndRoleQuestions.role.labelKey} tagName={'h4'} />
          <FormTextField
            textFieldProps={{
              type: 'text',
            }}
            name={'role'}
            rules={{ required: intl.formatMessage({ id: 'common.error.requiredField' }) }}
          />
        </div>

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <Button variant={'contained'} type={'submit'}>
            <FormattedMessage id={'cta.continue'} />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
